import React from 'react';
import { Solicitud } from '@/types/solicitud';
import { User } from '@/types/user';
import { Empresa } from '@/types/empresa';
import { Switch } from '@/components/ui/Switch';
import { usePlazosDisponibles } from '@/hooks/usePlazosDisponibles';
import { MontoInput } from '@/components/ui/MontoInput';
import { useAuth } from '@/hooks/useAuth';

const PRODUCTO_OPTIONS = ['Nómina', 'Adelanto de Nómina', 'Enganche Hipotecario'];
const ANTIGUEDAD_OPTIONS = ['0 - 6 meses', '6 meses - 1 año', '1 año - 2 años', 'Más de 2 años'];
const POLIZA_OPTIONS = ['Ninguna', '25000', '50000', '75000', '100000'];
const RECHAZO_RIESGOS_OPTIONS = [
  'Clave 100', 'Clave 101', 'Falta Solicitud', 'Falta Identificación',
  'Falta Comprobante de Domicilio', 'Es PEP', 'Es Persona Bloqueada',
  'Falta Aval', 'Buro del Aval', 'Falta de Liquidez', 'Mínimo Solicitado',
  'Nivel de Riesgo', 'Firma Apocrifa', 'Firma Digital', 'Contrato No Pasa',
  'Monto excede políticas', 'Por Políticas',
];

interface SolicitarVoBoSectionProps {
  localSolicitud: Solicitud;
  setLocalSolicitud: React.Dispatch<React.SetStateAction<Solicitud | null>>;
  setHasChanges: (v: boolean) => void;
  asesores: User[];
  empresas: Empresa[];
  loadingEmpresas?: boolean;
  onAsesorFinalChange?: (newAsesorId: string) => void;
  toggleDateField: (field: keyof Solicitud, enabled: boolean) => Promise<void>;
  updatingField: string | null;
}

export function SolicitarVoBoSection({
  localSolicitud,
  setLocalSolicitud,
  setHasChanges,
  asesores,
  empresas,
  loadingEmpresas,
  onAsesorFinalChange,
  toggleDateField,
}: SolicitarVoBoSectionProps) {
  const { isFieldAllowed } = useAuth();
  const { plazos: plazosOptions } = usePlazosDisponibles();
  const canEditInheritedID = isFieldAllowed('inheritedID');
  const canSeeRegion = isFieldAllowed('regionNombre');
  const canEditClienteEmpresa = isFieldAllowed('clienteEmpresa');
  const canEditClienteNombre = isFieldAllowed('clienteNombre');
  const canEditClienteApellidoPaterno = isFieldAllowed('clienteApellidoPaterno');
  const canEditClienteApellidoMaterno = isFieldAllowed('clienteApellidoMaterno');
  const canEditClienteNuevoRenovacion = isFieldAllowed('clienteNuevoRenovacion');
  const canEditClientePuesto = isFieldAllowed('clientePuesto');
  const canEditClienteAntiguedad = isFieldAllowed('clienteAntiguedad');
  const canEditClienteNombreJefe = isFieldAllowed('clienteNombreJefe');
  const canEditClienteCorreoJefe = isFieldAllowed('clienteCorreoJefe');
  const canEditClienteCorreo = isFieldAllowed('clienteCorreo');
  const canEditProducto = isFieldAllowed('producto');
  const canEditPolizaSeguro = isFieldAllowed('polizaSeguro');
  const canEditMontoSolicitado = isFieldAllowed('montoSolicitado');
  const canEditPlazoSolicitado = isFieldAllowed('plazoSolicitado');
  const canEditFechaEntregaARiesgos = isFieldAllowed('fechaEntregaARiesgos');
  const canEditFechaAnalisisConluido = isFieldAllowed('fechaAnalisisConluido');
  const canEditFechaPrimerSolicitudVoBo = isFieldAllowed('fechaPrimerSolicitudVoBo');
  const canEditFechaSegundaSolicitudVoBo = isFieldAllowed('fechaSegundaSolicitudVoBo');
  const canEditFechaTercerSolicitudVoBo = isFieldAllowed('fechaTercerSolicitudVoBo');
  const canEditFechaVoBo = isFieldAllowed('fechaVoBo');
  const canEditSolicitudCausaRechazoRiesgos = isFieldAllowed('solicitudCausaRechazoRiesgos');
  const canEditFechaRechazoRiesgos = isFieldAllowed('fechaRechazoRiesgos');
  const canSeeSolicitudEstatus = isFieldAllowed('solicitudEstatus');
  const canSeeDocumentos = isFieldAllowed('documentos');
  const canAddArchivos = isFieldAllowed('addArchivos');
  const canListaArchivos = isFieldAllowed('listaArchivos');

  const labelCls = "px-3 py-2 text-right font-medium text-gray-600 border border-gray-200 w-1/3";
  const inputCls = "w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500";
  const readOnlyCls = "w-full px-2 py-1 border border-gray-300 rounded bg-gray-50";

  const update = (field: string, value: any) => {
    setLocalSolicitud({ ...localSolicitud, [field]: value } as Solicitud);
    setHasChanges(true);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <tbody>
          {/* Asesor Final */}
          <tr>
            <td className={labelCls}>Asesor Final</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              {canEditInheritedID ? (
                <select className={inputCls} value={localSolicitud.inheritedID || ''} onChange={(e) => onAsesorFinalChange ? onAsesorFinalChange(e.target.value) : update('inheritedID', e.target.value)}>
                  <option value="">Seleccionar asesor...</option>
                  {asesores.map(a => (
                    <option key={a.ownerID} value={a.ownerID}>{a.name || a.userName}</option>
                  ))}
                </select>
              ) : (
                <input type="text" className={readOnlyCls} value={asesores.find(a => a.ownerID === localSolicitud.inheritedID)?.name || asesores.find(a => a.ownerID === localSolicitud.inheritedID)?.userName || localSolicitud.inheritedID || ''} readOnly />
              )}
            </td>
          </tr>

          {/* Region */}
          {canSeeRegion && (
          <tr>
            <td className={labelCls}>Region</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={readOnlyCls} value={localSolicitud.regionNombre || ''} readOnly />
            </td>
          </tr>
          )}

          {/* Producto */}
          {canEditProducto && (
          <tr>
            <td className={labelCls}>Producto</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select className={inputCls} value={localSolicitud.producto || ''} onChange={(e) => update('producto', e.target.value)}>
                <option value="">Seleccionar...</option>
                {PRODUCTO_OPTIONS.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </td>
          </tr>
          )}

          {/* Empresa */}
          {canEditClienteEmpresa && (
          <tr>
            <td className={labelCls}>Empresa</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              {loadingEmpresas ? (
                <div className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50 text-gray-500 flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Cargando empresas...
                </div>
              ) : empresas.length === 0 ? (
                <div className="w-full px-2 py-1 border border-yellow-300 rounded bg-yellow-50 text-yellow-700 text-sm">
                  No hay empresas asignadas a este asesor
                </div>
              ) : (
                <select className={inputCls} value={localSolicitud.clienteEmpresa || ''} onChange={(e) => {
                  const nombre = e.target.value;
                  const emp = empresas.find(em => em.nombre === nombre);
                  setLocalSolicitud({
                    ...localSolicitud,
                    clienteEmpresa: nombre,
                    regionNombre: emp?.region || '',
                    voboRequerido: emp?.voboRequerido || '',
                    clientePagadora: emp?.empresaPagadora || '',
                    clienteTipoEmpresa: emp?.tipo || '',
                    clienteNivelRiesgoEmpresa: emp?.nivel || '',
                  } as Solicitud);
                  setHasChanges(true);
                }}>
                  <option value="">Seleccionar empresa...</option>
                  {empresas.map(emp => (
                    <option key={emp.id || emp.nombre} value={emp.nombre}>{emp.nombre}</option>
                  ))}
                </select>
              )}
            </td>
          </tr>
          )}

          {/* Nombre */}
          {canEditClienteNombre && (
          <tr>
            <td className={labelCls}>Nombre</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={inputCls} value={localSolicitud.clienteNombre || ''} onChange={(e) => update('clienteNombre', e.target.value)} />
            </td>
          </tr>
          )}

          {/* Apellido Paterno */}
          {canEditClienteApellidoPaterno && (
          <tr>
            <td className={labelCls}>Apellido Paterno</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={inputCls} value={localSolicitud.clienteApellidoPaterno || ''} onChange={(e) => update('clienteApellidoPaterno', e.target.value)} />
            </td>
          </tr>
          )}

          {/* Apellido Materno */}
          {canEditClienteApellidoMaterno && (
          <tr>
            <td className={labelCls}>Apellido Materno</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={inputCls} value={localSolicitud.clienteApellidoMaterno || ''} onChange={(e) => update('clienteApellidoMaterno', e.target.value)} />
            </td>
          </tr>
          )}

          {/* Correo del Cliente */}
          {canEditClienteCorreo && (
          <tr>
            <td className={labelCls}>Correo del Cliente</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="email" className={inputCls} value={localSolicitud.clienteCorreo || ''} onChange={(e) => update('clienteCorreo', e.target.value)} placeholder="correo@ejemplo.com" />
            </td>
          </tr>
          )}

          {/* Nuevo/Renovación */}
          {canEditClienteNuevoRenovacion && (
          <tr>
            <td className={labelCls}>Nuevo/Renovación</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select className={inputCls} value={localSolicitud.clienteNuevoRenovacion || ''} onChange={(e) => update('clienteNuevoRenovacion', e.target.value)}>
                <option value="">Seleccionar...</option>
                <option value="Nuevo">Nuevo</option>
                <option value="Renovación">Renovación</option>
              </select>
            </td>
          </tr>
          )}

          {/* Puesto */}
          {canEditClientePuesto && (
          <tr>
            <td className={labelCls}>Puesto</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={inputCls} value={localSolicitud.clientePuesto || ''} onChange={(e) => update('clientePuesto', e.target.value)} />
            </td>
          </tr>
          )}

          {/* Antigüedad */}
          {canEditClienteAntiguedad && (
          <tr>
            <td className={labelCls}>Antigüedad</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select className={inputCls} value={localSolicitud.clienteAntiguedad || ''} onChange={(e) => update('clienteAntiguedad', e.target.value)}>
                <option value="">Seleccionar...</option>
                {ANTIGUEDAD_OPTIONS.map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </td>
          </tr>
          )}

          {/* Nombre de Jefe Inmediato */}
          {canEditClienteNombreJefe && (
          <tr>
            <td className={labelCls}>Nombre de Jefe Inmediato</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={inputCls} value={localSolicitud.clienteNombreJefe || ''} onChange={(e) => update('clienteNombreJefe', e.target.value)} />
            </td>
          </tr>
          )}

          {/* Correo de Jefe Inmediato */}
          {canEditClienteCorreoJefe && (
          <tr>
            <td className={labelCls}>Correo de Jefe Inmediato</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={inputCls} value={localSolicitud.clienteCorreoJefe || ''} onChange={(e) => update('clienteCorreoJefe', e.target.value)} />
            </td>
          </tr>
          )}

          {/* Poliza de Seguro - No aplica para Adelanto de Nómina */}
          {canEditPolizaSeguro && localSolicitud.producto !== 'Adelanto de Nómina' && (
          <tr>
            <td className={labelCls}>Poliza de Seguro</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select className={inputCls} value={localSolicitud.polizaSeguro || ''} onChange={(e) => update('polizaSeguro', e.target.value)}>
                <option value="">Seleccionar...</option>
                {POLIZA_OPTIONS.map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </td>
          </tr>
          )}

          {/* Monto Solicitado ($) */}
          {canEditMontoSolicitado && (
          <tr>
            <td className={labelCls}>Monto Solicitado ($)</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <MontoInput className={inputCls} value={localSolicitud.montoSolicitado} onChange={(n) => update('montoSolicitado', n)} />
            </td>
          </tr>
          )}

          {/* Plazo Solicitado en meses */}
          {canEditPlazoSolicitado && (
          <tr>
            <td className={labelCls}>Plazo Solicitado en meses</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select className={inputCls} value={localSolicitud.plazoSolicitado || ''} onChange={(e) => update('plazoSolicitado', e.target.value)}>
                <option value="">Seleccionar...</option>
                {plazosOptions.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </td>
          </tr>
          )}

          {/* Separador: Fechas y Control */}
          <tr className="bg-gray-100">
            <td colSpan={3} className="px-3 py-2 font-bold text-gray-700 border border-gray-300">Control de Fechas</td>
          </tr>

          {/* Entrega a Riesgos */}
          {canEditFechaEntregaARiesgos && (
          <tr>
            <td className={labelCls}>Entrega a Riesgos</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaEntregaARiesgos || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaEntregaARiesgos} onChange={(checked) => toggleDateField('fechaEntregaARiesgos' as keyof Solicitud, checked)} color="blue" />
            </td>
          </tr>
          )}

          {/* Análisis Concluído */}
          {canEditFechaAnalisisConluido && (
          <tr>
            <td className={labelCls}>Análisis Concluído</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaAnalisisConluido || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaAnalisisConluido} onChange={(checked) => toggleDateField('fechaAnalisisConluido' as keyof Solicitud, checked)} color="blue" />
            </td>
          </tr>
          )}

          {/* 1er Solicitud VoBo */}
          {canEditFechaPrimerSolicitudVoBo && (
          <tr>
            <td className={labelCls}>1er Solicitud VoBo</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaPrimerSolicitudVoBo || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaPrimerSolicitudVoBo} onChange={(checked) => toggleDateField('fechaPrimerSolicitudVoBo' as keyof Solicitud, checked)} color="blue" />
            </td>
          </tr>
          )}

          {/* 2da Solicitud VoBo */}
          {canEditFechaSegundaSolicitudVoBo && (
          <tr>
            <td className={labelCls}>2da Solicitud VoBo</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaSegundaSolicitudVoBo || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaSegundaSolicitudVoBo} onChange={(checked) => toggleDateField('fechaSegundaSolicitudVoBo' as keyof Solicitud, checked)} color="blue" />
            </td>
          </tr>
          )}

          {/* 3er Solicitud VoBo */}
          {canEditFechaTercerSolicitudVoBo && (
          <tr>
            <td className={labelCls}>3er Solicitud VoBo</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaTercerSolicitudVoBo || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaTercerSolicitudVoBo} onChange={(checked) => toggleDateField('fechaTercerSolicitudVoBo' as keyof Solicitud, checked)} color="blue" />
            </td>
          </tr>
          )}

          {/* Recibe VoBo */}
          {canEditFechaVoBo && (
          <tr>
            <td className={labelCls}>Recibe VoBo</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaVoBo || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaVoBo} onChange={(checked) => toggleDateField('fechaVoBo' as keyof Solicitud, checked)} color="blue" />
            </td>
          </tr>
          )}

          {/* Riesgos rechaza por */}
          {canEditSolicitudCausaRechazoRiesgos && (
          <tr>
            <td className={labelCls}>Riesgos rechaza por</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select className={inputCls} value={localSolicitud.motivoNoCierre || ''} onChange={(e) => update('motivoNoCierre', e.target.value)}>
                <option value="">Seleccionar...</option>
                {RECHAZO_RIESGOS_OPTIONS.map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </td>
          </tr>
          )}

          {/* Rechazado Riesgos */}
          {canEditFechaRechazoRiesgos && (
          <tr>
            <td className={labelCls}>Rechazado Riesgos</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaRechazoRiesgos || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaRechazoRiesgos} onChange={(checked) => toggleDateField('fechaRechazoRiesgos' as keyof Solicitud, checked)} color="blue" />
            </td>
          </tr>
          )}

          {/* Estatus */}
          {canSeeSolicitudEstatus && (
          <tr>
            <td className={labelCls}>Estatus</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={readOnlyCls} value={localSolicitud.solicitudEstatus || ''} readOnly />
            </td>
          </tr>
          )}

          {/* Link Documentos */}
          {canSeeDocumentos && (
          <tr>
            <td className={labelCls}>Link Documentos</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <div className="flex gap-2">
                {canAddArchivos ? (
                  <input type="text" className={inputCls + ' flex-1'} value={localSolicitud.documentos || ''} onChange={(e) => update('documentos', e.target.value)} placeholder="https://drive.google.com/..." />
                ) : (
                  <input type="text" className={readOnlyCls + ' flex-1'} value={localSolicitud.documentos || ''} readOnly />
                )}
                {canListaArchivos && localSolicitud.documentos && (
                  <button type="button" className="px-3 py-1 border border-gray-400 rounded bg-gray-100 hover:bg-gray-200 text-xs whitespace-nowrap" onClick={() => window.open(localSolicitud.documentos, '_blank')}>Ver</button>
                )}
              </div>
            </td>
          </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
